import { useState } from 'react';
import { NJButton, NJSegmentedControl, NJSegmentedControlButton, NJText } from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { UMR } from '../../urbanmap/_components';
import { readViewerFromUrl } from './ViewerSwitcher';
import { BRICKS, ReviewBlock, ReviewPlanSurface } from './WireframeSurfaces';
import type { ReviewBrick, ReviewOrigin, ReviewSection, ReviewVariant } from './wireframe.types';

const space = 'var(--nj-semantic-size-spacing-12)';
const SECTIONS: { id: ReviewSection; label: string }[] = [
  { id: 'params', label: 'Paramètres du projet' }, { id: 'tools', label: 'Outils UM' },
  { id: 'plans', label: 'Plans de dev' }, { id: 'hypotheses', label: 'Hypothèses' },
  { id: 'optimization', label: 'Optimisation' }, { id: 'exchange', label: 'Import-export' },
];
const PLANS = ['Chaud — référence', 'Chaud — ambitieux', 'Froid — îlot hospitalier'];

function VariantPicker({ label, value, onChange }: {
  label: string; value: ReviewVariant; onChange: (value: ReviewVariant) => void;
}) {
  return <div>
    <NJText scale="sm">{label}</NJText>
    <NJSegmentedControl label={label} value={value} size="sm" onChange={(next) => {
      if (next === 'A' || next === 'B') onChange(next);
    }}>
      <NJSegmentedControlButton label="A · recommandée" value="A" />
      <NJSegmentedControlButton label="B · alternative" value="B" />
    </NJSegmentedControl>
  </div>;
}

export function WireframeReview() {
  const [section, setSection] = useState<ReviewSection>('plans');
  const [brick, setBrick] = useState<ReviewBrick>('buildings');
  const [activePlan, setActivePlan] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const [full, setFull] = useState(false);
  const [viewVariant, setViewVariant] = useState<ReviewVariant>('A');
  const [returnVariant, setReturnVariant] = useState<ReviewVariant>('A');
  const [origin, setOrigin] = useState<ReviewOrigin | null>(null);
  const [exportPlan, setExportPlan] = useState('');
  const viewer = readViewerFromUrl();
  const canWrite = viewer !== 'read';
  const familyTitle = origin?.family ?? 'Catalogue des six familles';
  const selectSection = (next: ReviewSection) => { setSection(next); setOrigin(null); };
  const openFamily = (family: string) => {
    setOrigin({ brick, family });
    if (returnVariant === 'A') setSection('hypotheses');
  };
  const backToPlan = () => {
    if (origin) setBrick(origin.brick);
    setSection('plans'); setOrigin(null);
  };
  const familyPanel = <div style={{ display: 'flex', flexDirection: 'column', gap: space }}>
    <ReviewBlock title={familyTitle}>
      Familles → jeux nommés → consulter/créer/modifier/dupliquer/supprimer selon droits existants.
      Valeurs, version, usages et snapshots préservés. Un seul éditeur, dans Hypothèses.
      Modifier un jeu ne sélectionne pas automatiquement ce jeu pour le plan.
    </ReviewBlock>
    {origin && <NJButton label={`Retour au plan · ${BRICKS.find((item) => item.id === origin.brick)?.label}`}
      variant="secondary" scale="sm" onClick={backToPlan} />}
  </div>;

  return <PrototypeStudioLayout title="Plan — Navigation projet · Revue basse fidélité 29/10">
    <div data-journey-review style={{ fontFamily: UMR.font, color: UMR.textStrong, background: UMR.white, padding: space }}>
      <NJText as="h1" scale="lg">Archive de revue — décisions PO validées le 29/10</NJText>
      <NJText scale="sm">
        Blocs gris illustratifs, aucune modification métier. Profil URL : {viewer}.
        E1 A et E2 A précisée sont intégrées dans la vue fonctionnelle sans ce paramètre. Les variantes ci-dessous sont conservées comme historique.
      </NJText>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: space, alignItems: 'start' }}>
        <VariantPicker label="E1 — Accès à la vue complète" value={viewVariant} onChange={(value) => { setViewVariant(value); setFull(false); }} />
        <VariantPicker label="E2 — Gestion d’un jeu et retour" value={returnVariant} onChange={(value) => {
          setReturnVariant(value); setOrigin(null); setSection('plans');
        }} />
      </div>
      <NJText scale="sm">
        E1-A : bouton Vue complète et retour carte · E1-B : modes permanents Carte/Synthèse.
        E2-A : famille puis retour explicite · E2-B : catalogue adjacent au plan.
      </NJText>
      <ReviewBlock title="Parcours numéroté">
        1. Ouvrir le projet → liste des plans / créer. 2. Bâtiments → choisir Demande/Importance → Confirmer.
        3. Tracé → lancer tracé puis densité. 4. Phaser → contrôler tableaux et graphiques.
        5. Récapitulatif → Import-export projet → choisir les plans.
      </ReviewBlock>
      <NJText scale="sm">Mes projets / Gentilly — extension réseau Arcueil-Gentilly / {SECTIONS.find((item) => item.id === section)?.label}</NJText>
      <div style={{ display: 'flex', alignItems: 'stretch', gap: space, minHeight: 420 }}>
        <nav aria-label="Rail proposé" style={{ flex: `0 0 ${UMR.size.panelWidth}px`, display: 'flex', flexDirection: 'column', gap: space }}>
          {SECTIONS.map((item) => <NJButton key={item.id} label={item.label} variant="secondary" scale="sm"
            aria-current={section === item.id ? 'page' : undefined} onClick={() => selectSection(item.id)} />)}
        </nav>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: space }}>
          {section === 'plans' && <>
            <div style={{ display: 'flex', gap: space, flexWrap: 'wrap', alignItems: 'center' }}>
              <NJButton label={collapsed ? 'Afficher la liste Plans de dev' : 'Replier la liste Plans de dev'}
                variant="secondary" scale="sm" onClick={() => setCollapsed(!collapsed)} />
              <NJText scale="sm">Plan actif : {activePlan || 'aucun — choisir dans la liste'}</NJText>
            </div>
            {!collapsed && <ReviewBlock title="Liste des plans existants et création">
              {PLANS.map((name) => <NJButton key={name} label={`Ouvrir · ${name}`} variant="secondary" scale="sm"
                onClick={() => { setActivePlan(name); setBrick('buildings'); setOrigin(null); }} />)}
              {' '}[Nom · chaud/froid · créer · dupliquer selon droits — illustration]
            </ReviewBlock>}
            {activePlan && <>
              <div aria-label="Bandeau horizontal du plan" style={{ display: 'flex', flexWrap: 'wrap', gap: space, alignItems: 'center' }}>
                {BRICKS.map((item) => <NJButton key={item.id} label={item.label} variant="secondary" scale="sm"
                  aria-current={brick === item.id ? 'step' : undefined} onClick={() => { setBrick(item.id); setOrigin(null); setFull(false); }} />)}
                <NJText scale="sm">[État non confirmé · Annuler / Confirmer selon droits]</NJText>
              </div>
              {(brick === 'buildings' || brick === 'phasing') && (viewVariant === 'A'
                ? <NJButton label={full ? 'Revenir à la carte' : 'Vue complète'} variant="secondary" scale="sm" onClick={() => setFull(!full)} />
                : <NJSegmentedControl label="Vue de la section" value={full ? 'synthesis' : 'map'} size="sm" onChange={(value) => setFull(value === 'synthesis')}>
                  <NJSegmentedControlButton label="Carte" value="map" />
                  <NJSegmentedControlButton label="Synthèse" value="synthesis" />
                </NJSegmentedControl>)}
              <div style={{ display: 'flex', gap: space, alignItems: 'start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <ReviewPlanSurface brick={brick} full={full} canWrite={canWrite} onFamily={openFamily}
                    onSst={() => selectSection('params')} onExport={() => { setExportPlan(activePlan); selectSection('exchange'); }} />
                </div>
                {origin && returnVariant === 'B' && <aside aria-label="Catalogue adjacent" style={{ flex: `0 0 ${UMR.size.panelWidth}px` }}>{familyPanel}</aside>}
              </div>
            </>}
          </>}
          {section === 'hypotheses' && familyPanel}
          {section === 'params' && <>
            <ReviewBlock title="Paramètres du projet + SST / locaux techniques">
              Paramètres existants puis positions communes à tous les plans. Import GeoJSON, origine conservée,
              points À vérifier, association bâtiment, aperçu de zone, validation individuelle et de zone.
              Maximum un local validé par bâtiment. Conflits de migration/import exposés ; remplacement confirmé.
              Points de départ réseau distincts.
            </ReviewBlock>
            {activePlan && <NJButton label="Retour au plan · Bâtiments" variant="secondary" scale="sm"
              onClick={() => { setBrick('buildings'); selectSection('plans'); }} />}
          </>}
          {section === 'tools' && <ReviewBlock title="Outils UM → Sélections (sans tableau de bâtiments)">
            Toutes les sélections · créer · résumé actif · plans utilisateurs. Plan actif : {activePlan || 'aucun'}.
            Carte : bâtiment du plan actif = couleur du plan, sinon couleur de la sélection ; légende explicite.
            Les outils existants restent accessibles. Aucun affichage multi-plans ni backend UM ajouté.
          </ReviewBlock>}
          {section === 'exchange' && <ReviewBlock title="Import-export au niveau projet">
            Plans à cocher : [liste des plans]. Présélection depuis Récapitulatif : {exportPlan || 'aucune'}.
            [Aucun sélectionné → téléchargement désactivé + explication].
            Toutes les sorties embarquent récap complet hypothèses + configuration projet ; courant/archives séparés,
            aucune archive inventée. CSV phasage et GeoJSON SST disponibles ici. Imports existants avec aperçu,
            validation/annulation et conflits conservés. Téléchargement non branché dans cette basse fidélité.
          </ReviewBlock>}
          {section === 'optimization' && <ReviewBlock title="Optimisation · V2">
            Placeholder existant conservé, algorithme non activé par cette refonte.
          </ReviewBlock>}
        </div>
      </div>
      <NJText scale="sm">À confirmer : E1 A/B · E2 A/B · contenu par élément du brief. Rien n'est validé automatiquement par un clic dans cette revue.</NJText>
    </div>
  </PrototypeStudioLayout>;
}
